import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Shield, Clock, CreditCard, MessageCircle, Store, Headphones } from "lucide-react";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";
import VehicleFilter from "../components/VehicleFilter";
import axios from "axios";
import { API } from "../App";

const HERO_IMAGE = "https://images.unsplash.com/photo-1756575527484-2839c593ed84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxtZWNoYW5pYyUyMHdvcmtpbmclMjBvbiUyMGNhciUyMGVuZ2luZSUyMGluJTIwZGFyayUyMGdhcmFnZXxlbnwwfHx8fDE3NzA2OTIxOTB8MA&ixlib=rb-4.1.0&q=85";

// ============================================
// FEATURE FLAG - Vehicle Search Visibility
// Set to true to show vehicle search, false to hide
// This allows easy reactivation in the future
// ============================================
const SHOW_VEHICLE_SEARCH = false;

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsappLink, setWhatsappLink] = useState("https://wa.me/16172560105?text=Hi%2C%20I%20need%20help%20finding%20auto%20parts.");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, bannersRes, settingsRes] = await Promise.all([
          axios.get(`${API}/products/featured`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/homepage-banners`),
          axios.get(`${API}/settings`).catch(() => ({ data: {} }))
        ]);
        setFeaturedProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setBanners(bannersRes.data || []);
        
        // Build WhatsApp link from settings
        if (settingsRes.data?.whatsapp) {
          const phoneNumber = settingsRes.data.whatsapp.replace(/[^0-9]/g, '');
          const message = encodeURIComponent("Hi, I need help finding auto parts.");
          setWhatsappLink(`https://wa.me/${phoneNumber}?text=${message}`);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    { icon: Truck, title: "Free Shipping", desc: "On orders over $100" },
    { icon: Shield, title: "Quality Guaranteed", desc: "Premium auto parts only" },
    { icon: Clock, title: "Fast Delivery", desc: "2-5 business days" },
    { icon: CreditCard, title: "Secure Payment", desc: "SSL encrypted checkout" },
  ];

  return (
    <div className="animate-fade-in" data-testid="home-page">
      {/* Hero Section - Premium Clean Design */}
      <section className="relative min-h-[620px] lg:min-h-[680px] flex items-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-testid="hero-section">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Content */}
            <div className="text-center lg:text-left">
              {/* Micro Badge */}
              <div className="inline-flex items-center gap-2 bg-yellow-500/15 text-yellow-400 px-4 py-2 rounded-full text-sm font-medium tracking-wide mb-8 animate-slide-up">
                <Store className="w-4 h-4" />
                <span>Auto Parts — In-Store Only</span>
              </div>
              
              {/* Main Headline - Premium Typography */}
              <h1 className="animate-slide-up" data-testid="hero-title">
                <span className="block text-white text-3xl md:text-4xl lg:text-[42px] font-semibold tracking-tight leading-snug" style={{ fontFamily: "'Montserrat', 'Inter', system-ui, sans-serif" }}>
                  Need Auto Parts?
                </span>
                <span className="block text-primary text-4xl md:text-5xl lg:text-[52px] font-bold tracking-tight mt-3 leading-tight" style={{ fontFamily: "'Montserrat', 'Inter', system-ui, sans-serif" }}>
                  Visit Our Store
                </span>
                <span className="block text-white text-2xl md:text-3xl lg:text-[32px] font-medium tracking-tight mt-2" style={{ fontFamily: "'Montserrat', 'Inter', system-ui, sans-serif" }}>
                  or <span className="text-green-400 font-semibold">Talk to a Seller</span>
                </span>
              </h1>
              
              {/* Supporting Text */}
              <p className="text-base md:text-lg text-gray-400 mt-8 max-w-lg mx-auto lg:mx-0 animate-slide-up stagger-1 leading-relaxed tracking-wide">
                Auto parts are currently available <span className="text-white font-medium">in-store only</span>.<br className="hidden sm:block" />
                Talk directly to a seller for fast help finding the right part.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mt-10 animate-slide-up stagger-2 justify-center lg:justify-start">
                {/* Primary CTA - WhatsApp */}
                <a 
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white font-semibold text-base px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                  data-testid="talk-to-seller-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Talk to a Seller
                </a>
                
                {/* Secondary CTA - Shop Accessories */}
                <Link 
                  to="/shop?category=accessories"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white font-medium text-base px-8 py-4 rounded-lg border border-white/20 hover:border-white/30 transition-all duration-300"
                  data-testid="shop-accessories-btn"
                >
                  Shop Accessories Online
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
              
              {/* Reinforcement Text */}
              <div className="mt-10 flex items-center gap-3 justify-center lg:justify-start text-gray-500 animate-slide-up stagger-3">
                <Headphones className="w-5 h-5 text-green-500" />
                <span className="text-sm tracking-wide">Get expert help instantly — our team is ready to assist</span>
              </div>
            </div>
            
            {/* Right Side - Clean Image Only */}
            <div className="hidden lg:block relative animate-slide-up stagger-2">
              <img 
                src={HERO_IMAGE} 
                alt="Professional auto parts assistance" 
                className="w-full h-[480px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Filter - HIDDEN BUT PRESERVED (Feature Flag Controlled) */}
      {SHOW_VEHICLE_SEARCH && (
        <section className="py-12 bg-gray-50" data-testid="vehicle-filter-section">
          <div className="container mx-auto px-4">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 text-center mb-6 uppercase tracking-tight">
              Find Parts For Your Vehicle
            </h2>
            <VehicleFilter />
          </div>
        </section>
      )}

      {/* Features Bar */}
      <section className="bg-white border-y border-gray-200 py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-4" data-testid={`feature-${index}`}>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-gray-900 font-semibold">{feature.title}</h3>
                  <p className="text-gray-500 text-sm">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Banners - Dynamic 2-Column Grid */}
      <section className="py-20 bg-white" data-testid="categories-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 uppercase tracking-tight">
              Shop By Category
            </h2>
            <p className="text-gray-600 mt-4 text-lg">Browse our extensive selection of quality auto parts</p>
          </div>

          {/* Clean 2-Column Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {banners.map((banner, index) => (
              <Link 
                key={banner.banner_id}
                to={banner.link} 
                className="group relative aspect-[16/9] rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
                data-testid={`category-banner-${index}`}
              >
                {/* Image with lazy loading */}
                <img 
                  src={banner.image_url} 
                  alt={banner.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                
                {/* Dark gradient overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                
                {/* Content overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase tracking-tight">
                    {banner.title}
                  </h3>
                  {banner.subtitle && (
                    <p className="text-gray-200 mt-2 text-sm md:text-base">
                      {banner.subtitle}
                    </p>
                  )}
                  <span className="inline-flex items-center text-white font-semibold mt-4 group-hover:translate-x-2 transition-transform">
                    {banner.button_text} <ArrowRight className="w-4 h-4 ml-2" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Fallback if no banners */}
          {banners.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              <p>No category banners configured. Visit Admin to add banners.</p>
            </div>
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-gray-50" data-testid="featured-products-section">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 uppercase tracking-tight">
                Featured Products
              </h2>
              <p className="text-gray-600 mt-2">Top-rated parts chosen by our experts</p>
            </div>
            <Link to="/shop" className="hidden md:inline-flex items-center text-primary hover:text-primary/80 font-semibold">
              View All <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white aspect-[3/4] skeleton rounded-lg"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.slice(0, 8).map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          )}

          <div className="text-center mt-12 md:hidden">
            <Link to="/shop">
              <Button className="btn-primary">View All Products</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Brand CTA Banner - Evergreen */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0B1F4D 0%, #123D91 100%)' }} data-testid="promo-section">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(30,99,255,0.12) 0%, transparent 70%)' }}></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-white uppercase tracking-tight">
            Quality Parts, Trusted Service
          </h2>
          <p className="text-[#DCE7FF] text-lg mt-4 max-w-2xl mx-auto">
            Browse our catalog online or talk to a seller for expert guidance on the right parts for your vehicle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
            <Link to="/shop">
              <Button className="font-bold uppercase tracking-wider px-10 py-4 text-lg" style={{ backgroundColor: '#FFD100', color: '#0B1F4D' }}>
                Browse Catalog
              </Button>
            </Link>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button className="font-bold uppercase tracking-wider px-10 py-4 text-lg" style={{ backgroundColor: '#22C55E', color: '#FFFFFF' }}>
                <MessageCircle className="w-5 h-5 mr-2" /> Talk to a Seller
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Video Section - Single Video */}
      <section className="py-20 bg-white" data-testid="video-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 uppercase tracking-tight">
              Product Spotlight
            </h2>
            <p className="text-gray-600 mt-4 text-lg">See our products in action</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden shadow-lg">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                title="Product Spotlight"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 uppercase tracking-tight">
            Ready to Upgrade Your Ride?
          </h2>
          <p className="text-gray-600 mt-4 max-w-xl mx-auto">
            Join thousands of satisfied customers who trust Brendolly's for their auto parts needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/shop">
              <Button className="btn-primary">Browse All Parts</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="btn-outline">Contact Us</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
